import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { loadExternalScript } from "../../utils/loadExternalScript";

const DEFAULT_IMAGE_HOST = "https://quiz.color-analysis.pro";
const DEFAULT_DEMO_IMAGE_PATH = "/facemesh_demo.png";
const FACEMESH_SOURCE_PROXY_PATH = "/api/facemesh/source";
const FACEMESH_V2_OVERLAY_UPLOAD_PATH = "/api/facemesh-v2/overlay";
const FACEMESH_V2_PROCESS_PATH = "/api/facemesh-v2/process-background";
const FACEMESH_V2_ANALYTICS_API_BASE =
  process.env.REACT_APP_FACEMESH_V2_ANALYTICS_API_BASE || "https://facemesh-codex.onrender.com";
const FACEMESH_V2_ANALYTICS_PATH = "/api/analytics/events";

const MEDIAPIPE_SCRIPTS = [
  "https://cdn.jsdelivr.net/npm/@mediapipe/drawing_utils/drawing_utils.js",
  "https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/face_mesh.js",
];

const DEFAULT_PROGRESS_STEPS = [
  { threshold: 10, label: "Analyzing facial geometry" },
  { threshold: 35, label: "Analyzing skin undertone balance" },
  { threshold: 60, label: "Analyzing eye contrast and clarity" },
  { threshold: 85, label: "Analyzing natural hair depth" },
  { threshold: 100, label: "Calibrating your personal color profile" },
];

const ANALYSIS_PROGRESS_DURATION_MS = 7000;
const CONTINUE_FALLBACK_DELAY_MS = 2000;
const AUTO_REDIRECT_DELAY_MS = 1000;
const MESH_CONNECTION_DENSITY_RATIO = 0.5;
const FACEMESH_V2_EXTENSION_RATIO = "0.22";
const ALLOWED_OVERLAY_MIME_TYPES = new Set(["image/webp", "image/png", "image/jpeg"]);
const OVERLAY_RENDER_MIME_CANDIDATES = ["image/webp", "image/png"];
const ANALYSIS_KEYFRAMES = [
  { time: 0, progress: 1 },
  { time: 900, progress: 10 },
  { time: 1700, progress: 10 },
  { time: 3000, progress: 35 },
  { time: 3700, progress: 35 },
  { time: 4800, progress: 60 },
  { time: 5400, progress: 60 },
  { time: 6300, progress: 85 },
  { time: 7000, progress: 98 },
];

const normalizeImageUrl = (rawUrl) => {
  if (!rawUrl) return "";
  if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
  if (rawUrl.startsWith("//")) return `https:${rawUrl}`;
  if (rawUrl.startsWith("/")) return `${DEFAULT_IMAGE_HOST}${rawUrl}`;
  return `${DEFAULT_IMAGE_HOST}/${rawUrl}`;
};

const unwrapImageParam = (rawValue) => {
  if (!rawValue) return "";

  const trimmed = String(rawValue).trim();
  if (!trimmed) return "";

  let decoded = trimmed;
  if (/%[0-9a-f]{2}/i.test(trimmed)) {
    try {
      decoded = decodeURIComponent(trimmed);
    } catch {
      decoded = trimmed;
    }
  }

  if (!/[<>]/.test(decoded) && !/href\s*=/.test(decoded)) {
    return decoded;
  }

  try {
    const doc = new DOMParser().parseFromString(decoded, "text/html");
    const anchor = doc.querySelector("a[href]");
    const href = anchor?.getAttribute("href")?.trim();
    if (href) return href;
  } catch {
    // Ignore parsing errors and fall back to regex / raw.
  }

  const hrefMatch = decoded.match(/href\s*=\s*["']([^"']+)["']/i);
  if (hrefMatch?.[1]) return hrefMatch[1].trim();

  return decoded;
};

const getAnalysisImageUrl = (normalizedUrl, apiBase = "") => {
  if (!normalizedUrl) return "";

  try {
    const parsedUrl = new URL(normalizedUrl);
    if (parsedUrl.hostname === "quiz.color-analysis.pro") {
      return `${apiBase}${FACEMESH_SOURCE_PROXY_PATH}?url=${encodeURIComponent(parsedUrl.toString())}`;
    }
    return parsedUrl.toString();
  } catch {
    return normalizedUrl;
  }
};

const buildContinueUrl = (baseUrl, search = "") => {
  if (!search) return baseUrl;
  if (baseUrl.includes("?")) {
    return `${baseUrl}&${search.replace(/^\?/, "")}`;
  }
  return `${baseUrl}${search}`;
};

const getProgressLabel = (progress, progressSteps) => {
  for (const step of progressSteps) {
    if (progress <= step.threshold) {
      return step.label;
    }
  }
  return progressSteps[progressSteps.length - 1].label;
};

const getCurrentStepIndex = (progress, progressSteps) => {
  for (let index = 0; index < progressSteps.length; index += 1) {
    if (progress <= progressSteps[index].threshold) {
      return index;
    }
  }
  return progressSteps.length - 1;
};

const getProgressByKeyframes = (elapsedMs) => {
  if (elapsedMs <= ANALYSIS_KEYFRAMES[0].time) {
    return ANALYSIS_KEYFRAMES[0].progress;
  }

  for (let i = 1; i < ANALYSIS_KEYFRAMES.length; i += 1) {
    const previous = ANALYSIS_KEYFRAMES[i - 1];
    const current = ANALYSIS_KEYFRAMES[i];
    if (elapsedMs <= current.time) {
      if (current.time === previous.time) {
        return current.progress;
      }
      const fraction = (elapsedMs - previous.time) / (current.time - previous.time);
      return previous.progress + (current.progress - previous.progress) * fraction;
    }
  }

  return ANALYSIS_KEYFRAMES[ANALYSIS_KEYFRAMES.length - 1].progress;
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image failed to load"));
    img.src = src;
  });

const assertImageUrlResolvable = (src) => {
  try {
    new URL(src, window.location.origin);
  } catch {
    throw new Error("Invalid image URL.");
  }
};

const shouldRetryWithDemoImage = (targetImageUrl, error) => {
  if (targetImageUrl === DEFAULT_DEMO_IMAGE_PATH) {
    return false;
  }

  const message = error?.message || "";
  return message === "Invalid image URL." || message === "Image failed to load";
};

const renderCanvasBlob = (canvas, mimeType) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error(`Failed to render overlay image as ${mimeType}.`));
          return;
        }
        resolve(blob);
      },
      mimeType,
      0.9
    );
  });

const normalizeOverlayBlob = async (blob, fallbackMimeType) => {
  if (!blob || blob.size === 0) {
    return null;
  }

  const normalizedMimeType = blob.type?.trim().toLowerCase() || fallbackMimeType;
  if (!ALLOWED_OVERLAY_MIME_TYPES.has(normalizedMimeType)) {
    return null;
  }

  if (blob.type?.trim()) {
    return { blob, mimeType: normalizedMimeType };
  }

  const buffer = await blob.arrayBuffer();
  return {
    blob: new Blob([buffer], { type: normalizedMimeType }),
    mimeType: normalizedMimeType,
  };
};

const canvasToUploadableBlob = async (canvas) => {
  for (const mimeType of OVERLAY_RENDER_MIME_CANDIDATES) {
    try {
      const blob = await renderCanvasBlob(canvas, mimeType);
      const normalized = await normalizeOverlayBlob(blob, mimeType);
      if (normalized) {
        return normalized;
      }
    } catch {
      // Try the next supported MIME candidate.
    }
  }

  throw new Error("Failed to render overlay image in a supported format.");
};

const serializeLandmarksCompact = (faceLandmarks) => {
  if (!Array.isArray(faceLandmarks)) return "[]";

  const compactLandmarks = faceLandmarks.map((point = {}) => ({
    x: Number(point.x) || 0,
    y: Number(point.y) || 0,
    z: Number(point.z) || 0,
  }));

  return JSON.stringify(compactLandmarks);
};

const assertCanvasReadable = (image) => {
  const probe = document.createElement("canvas");
  probe.width = 1;
  probe.height = 1;
  const probeCtx = probe.getContext("2d");
  probeCtx.drawImage(image, 0, 0, 1, 1);
  probeCtx.getImageData(0, 0, 1, 1);
};

const ensureMediaPipeReady = async () => {
  await Promise.all(MEDIAPIPE_SCRIPTS.map((src) => loadExternalScript(src)));
  if (!window.FaceMesh || !window.drawConnectors) {
    throw new Error("MediaPipe scripts are loaded but API is unavailable.");
  }
};

const detectLandmarks = (image) =>
  new Promise(async (resolve, reject) => {
    try {
      const faceMesh = new window.FaceMesh({
        locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
      });
      faceMesh.setOptions({
        staticImageMode: true,
        maxNumFaces: 1,
        refineLandmarks: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });
      faceMesh.onResults((results) => {
        const firstFace = results?.multiFaceLandmarks?.[0];
        if (!firstFace) {
          reject(new Error("No face detected on the provided image."));
          return;
        }
        resolve(firstFace);
      });
      await faceMesh.send({ image });
    } catch (error) {
      reject(error);
    }
  });

const createJobId = () => {
  const now = new Date();
  const yyyy = now.getUTCFullYear();
  const mm = `${now.getUTCMonth() + 1}`.padStart(2, "0");
  const dd = `${now.getUTCDate()}`.padStart(2, "0");
  const randomPart =
    window.crypto?.randomUUID?.() ||
    "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
      const random = Math.floor(Math.random() * 16);
      const next = character === "x" ? random : (random & 0x3) | 0x8;
      return next.toString(16);
    });
  return `${yyyy}${mm}${dd}-${randomPart}`;
};

const createSessionId = () =>
  window.crypto?.randomUUID?.() ||
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const next = character === "x" ? random : (random & 0x3) | 0x8;
    return next.toString(16);
  });

const isValidUrl = (rawValue) => {
  if (!rawValue) return false;
  try {
    new URL(rawValue, window.location.origin);
    return true;
  } catch (_) {
    return false;
  }
};

const getAnalyticsApiBase = () => FACEMESH_V2_ANALYTICS_API_BASE.replace(/\/+$/, "");

const buildAbsoluteUrl = (rawUrl) => new URL(rawUrl, window.location.origin).toString();

const isDemoImageUrl = (rawUrl) => {
  if (!rawUrl) return false;

  try {
    const parsedUrl = new URL(rawUrl, window.location.origin);
    return parsedUrl.pathname === DEFAULT_DEMO_IMAGE_PATH;
  } catch (_) {
    return rawUrl === DEFAULT_DEMO_IMAGE_PATH;
  }
};

const drawOverlay = (ctx, landmarks, progress, reducedMeshConnections) => {
  if (!ctx || !landmarks) return;

  const draw = window.drawConnectors;
  if (!draw) return;

  if (progress >= 10) {
    draw(ctx, landmarks, reducedMeshConnections || window.FACEMESH_TESSELATION, { color: "#FFFFFF", lineWidth: 1 });
  }
  if (progress >= 35) {
    draw(ctx, landmarks, window.FACEMESH_FACE_OVAL, { color: "#FACC15", lineWidth: 3 });
  }
  if (progress >= 60) {
    draw(ctx, landmarks, window.FACEMESH_LEFT_EYE, { color: "#22D3EE", lineWidth: 2 });
    draw(ctx, landmarks, window.FACEMESH_RIGHT_EYE, { color: "#22D3EE", lineWidth: 2 });
    draw(ctx, landmarks, window.FACEMESH_LEFT_IRIS, { color: "#22D3EE", lineWidth: 2 });
    draw(ctx, landmarks, window.FACEMESH_RIGHT_IRIS, { color: "#22D3EE", lineWidth: 2 });
  }
  if (progress >= 85) {
    draw(ctx, landmarks, window.FACEMESH_LEFT_EYEBROW, { color: "#22C55E", lineWidth: 2 });
    draw(ctx, landmarks, window.FACEMESH_RIGHT_EYEBROW, { color: "#22C55E", lineWidth: 2 });
  }
  if (progress >= 95) {
    draw(ctx, landmarks, window.FACEMESH_LIPS, { color: "#EF4444", lineWidth: 2 });
  }
};

const createOverlayCanvas = (image, landmarks, reducedMeshConnections, progress = 100) => {
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  drawOverlay(ctx, landmarks, progress, reducedMeshConnections);
  return canvas;
};

const DEFAULT_CONFIG = {
  analyticsPrefix: "facemesh_v2",
  analyticsEventPrefix: "facemesh_v2",
  continueUrl: "https://payment.color-analysis.pro/colortype-3month-29-upsell",
  missingImageHelpPath: "/facemesh-v2",
  pagePath: "/facemesh-v2",
  progressSteps: DEFAULT_PROGRESS_STEPS,
};

const buildEventName = (analyticsPrefix, suffix) => `${analyticsPrefix}_${suffix}`;
const buildSessionStorageKey = (analyticsPrefix) => `${analyticsPrefix}_session_id`;

const EMPTY_ENQUEUE_ERROR_DETAILS = Object.freeze({
  errorCode: "",
  errorStage: "",
  httpStatus: null,
  errorKind: "",
  errorMessage: "",
});

const getOrCreateSessionId = (sessionStorageKey) => {
  try {
    const existing = (window.sessionStorage.getItem(sessionStorageKey) || "").trim();
    if (existing) return existing;
    const created = createSessionId();
    window.sessionStorage.setItem(sessionStorageKey, created);
    return created;
  } catch (_) {
    return createSessionId();
  }
};

const sendFacemeshV2AnalyticsEvent = ({
  analyticsPrefix,
  event_name,
  session_id,
  job_id,
  client_reference_id,
  page_path,
  source_image_present,
  source_image_valid,
  metadata_json = {},
  keepalive = false,
}) => {
  if (!event_name || !session_id) return;

  fetch(`${getAnalyticsApiBase()}${FACEMESH_V2_ANALYTICS_PATH}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      event_name,
      session_id,
      job_id,
      client_reference_id,
      page_path,
      source_image_present,
      source_image_valid,
      metadata_json,
    }),
    keepalive,
  }).catch((error) => {
    console.warn(`${analyticsPrefix} analytics event failed: ${event_name}`, error);
  });
};

const parseJsonSafely = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
};

const normalizeEnqueueErrorDetails = ({
  errorCode = "",
  errorStage = "",
  httpStatus = null,
  errorKind = "",
  errorMessage = "",
}) => ({
  errorCode,
  errorStage,
  httpStatus: typeof httpStatus === "number" ? httpStatus : null,
  errorKind,
  errorMessage,
});

const FaceMeshAsyncV2Page = ({
  analyticsPrefix = DEFAULT_CONFIG.analyticsPrefix,
  analyticsEventPrefix = DEFAULT_CONFIG.analyticsEventPrefix,
  continueUrl = DEFAULT_CONFIG.continueUrl,
  missingImageHelpPath = DEFAULT_CONFIG.missingImageHelpPath,
  pagePath = DEFAULT_CONFIG.pagePath,
  progressSteps = DEFAULT_CONFIG.progressSteps,
}) => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSavingOverlay, setIsSavingOverlay] = useState(false);
  const [saveOverlayError, setSaveOverlayError] = useState("");
  const [isQueueingJob, setIsQueueingJob] = useState(false);
  const [queueJobError, setQueueJobError] = useState("");
  const [enqueueErrorDetails, setEnqueueErrorDetails] = useState(EMPTY_ENQUEUE_ERROR_DETAILS);
  const [queuedJobId, setQueuedJobId] = useState("");
  const [isContinueFallbackUnlocked, setIsContinueFallbackUnlocked] = useState(false);
  const [isRedirectPending, setIsRedirectPending] = useState(false);
  const [landmarks, setLandmarks] = useState(null);
  const [imageElement, setImageElement] = useState(null);
  const stagedTimerRef = useRef(null);
  const completionTimerRef = useRef(null);
  const continueFallbackTimerRef = useRef(null);
  const autoRedirectTimerRef = useRef(null);
  const progressValueRef = useRef(0);
  const analysisReadyRef = useRef(false);
  const completionStartedRef = useRef(false);
  const autoStartedUrlRef = useRef("");
  const autoRedirectedRunIdRef = useRef(0);
  const analysisRunIdRef = useRef(0);
  const backendStartedRunIdRef = useRef(0);
  const uploadedRunIdRef = useRef(0);
  const queuedRunIdRef = useRef(0);
  const analyzedSourceUrlRef = useRef("");
  const usedDemoImageRunIdRef = useRef(0);
  const reducedMeshConnectionsRef = useRef(null);
  const canvasRef = useRef(null);
  const pageViewKeyRef = useRef("");
  const imageDetectedKeyRef = useRef("");
  const sessionStorageKey = useMemo(() => buildSessionStorageKey(analyticsPrefix), [analyticsPrefix]);
  const sessionIdRef = useRef(
    typeof window !== "undefined" ? getOrCreateSessionId(sessionStorageKey) : ""
  );

  const rawImageUrlParam = searchParams.get("img") || searchParams.get("image") || "";
  const resolvedImageUrlParam = useMemo(() => unwrapImageParam(rawImageUrlParam), [rawImageUrlParam]);
  const clientReferenceId = searchParams.get("client_reference_id") || "";
  const rawApiBase = searchParams.get("apiBase") || "";
  const apiBase = useMemo(() => rawApiBase.replace(/\/+$/, ""), [rawApiBase]);
  const imageUrl = useMemo(
    () => (resolvedImageUrlParam ? normalizeImageUrl(resolvedImageUrlParam) : DEFAULT_DEMO_IMAGE_PATH),
    [resolvedImageUrlParam]
  );
  const analysisImageUrl = useMemo(() => getAnalysisImageUrl(imageUrl, apiBase), [apiBase, imageUrl]);
  const searchKey = useMemo(() => searchParams.toString(), [searchParams]);
  const currentStepIndex = useMemo(() => getCurrentStepIndex(progress, progressSteps), [progress, progressSteps]);
  const hasQueuedBackend = Boolean(queuedJobId) && !isSavingOverlay && !isQueueingJob;
  const canContinue = status === "done" && (hasQueuedBackend || isContinueFallbackUnlocked);
  const hasProvidedImageUrl = Boolean(rawImageUrlParam.trim());
  const hasValidProvidedImageUrl = hasProvidedImageUrl && isValidUrl(imageUrl);
  const usesSourceProxy = analysisImageUrl.includes(FACEMESH_SOURCE_PROXY_PATH);

  const pageViewEventName = useMemo(
    () => buildEventName(analyticsEventPrefix, "page_view"),
    [analyticsEventPrefix]
  );
  const imageDetectedEventName = useMemo(
    () => buildEventName(analyticsEventPrefix, "image_url_detected"),
    [analyticsEventPrefix]
  );
  const continueEventName = useMemo(
    () => buildEventName(analyticsEventPrefix, "continue_clicked"),
    [analyticsEventPrefix]
  );
  const enqueueFailedEventName = useMemo(
    () => buildEventName(analyticsEventPrefix, "enqueue_failed"),
    [analyticsEventPrefix]
  );
  const missingImageMessage = useMemo(
    () => `Missing image URL. Open ${missingImageHelpPath}?img=https://quiz.color-analysis.pro/image/your-file.jpeg`,
    [missingImageHelpPath]
  );

  const updateProgress = (value) => {
    const next = Math.max(0, Math.min(100, Math.round(value)));
    progressValueRef.current = next;
    setProgress(next);
  };

  const clearProgressTimers = () => {
    if (stagedTimerRef.current) {
      window.clearInterval(stagedTimerRef.current);
      stagedTimerRef.current = null;
    }
    if (completionTimerRef.current) {
      window.clearInterval(completionTimerRef.current);
      completionTimerRef.current = null;
    }
    if (continueFallbackTimerRef.current) {
      window.clearTimeout(continueFallbackTimerRef.current);
      continueFallbackTimerRef.current = null;
    }
    if (autoRedirectTimerRef.current) {
      window.clearTimeout(autoRedirectTimerRef.current);
      autoRedirectTimerRef.current = null;
    }
  };

  useEffect(() => clearProgressTimers, []);

  useEffect(() => {
    if (!sessionIdRef.current) return;
    if (pageViewKeyRef.current === searchKey) return;

    pageViewKeyRef.current = searchKey;
    sendFacemeshV2AnalyticsEvent({
      analyticsPrefix,
      event_name: pageViewEventName,
      session_id: sessionIdRef.current,
      client_reference_id: clientReferenceId || undefined,
      page_path: pagePath,
      source_image_present: hasProvidedImageUrl,
      source_image_valid: hasValidProvidedImageUrl,
      metadata_json: {
        api_base: apiBase || window.location.origin,
        client_reference_id_present: Boolean(clientReferenceId),
        using_demo_image: !hasProvidedImageUrl,
      },
    });
  }, [
    analyticsPrefix,
    apiBase,
    clientReferenceId,
    hasProvidedImageUrl,
    hasValidProvidedImageUrl,
    pagePath,
    pageViewEventName,
    searchKey,
  ]);

  useEffect(() => {
    if (!sessionIdRef.current) return;
    if (!hasValidProvidedImageUrl) return;
    if (imageDetectedKeyRef.current === searchKey) return;

    imageDetectedKeyRef.current = searchKey;
    sendFacemeshV2AnalyticsEvent({
      analyticsPrefix,
      event_name: imageDetectedEventName,
      session_id: sessionIdRef.current,
      client_reference_id: clientReferenceId || undefined,
      page_path: pagePath,
      source_image_present: true,
      source_image_valid: true,
      metadata_json: {
        source_host: (() => {
          try {
            return new URL(imageUrl).hostname;
          } catch (_) {
            return "";
          }
        })(),
        via_source_proxy: usesSourceProxy,
      },
    });
  }, [
    analyticsPrefix,
    clientReferenceId,
    hasValidProvidedImageUrl,
    imageDetectedEventName,
    imageUrl,
    pagePath,
    searchKey,
    usesSourceProxy,
  ]);

  const getReducedMeshConnections = () => {
    if (reducedMeshConnectionsRef.current) {
      return reducedMeshConnectionsRef.current;
    }

    const fullMeshConnections = window.FACEMESH_TESSELATION;
    if (!fullMeshConnections) return null;

    const meshConnectionsArray = Array.isArray(fullMeshConnections)
      ? fullMeshConnections
      : Array.from(fullMeshConnections);

    const reducedMeshConnections = meshConnectionsArray.filter((_, index) => {
      return index % Math.round(1 / MESH_CONNECTION_DENSITY_RATIO) === 0;
    });

    reducedMeshConnectionsRef.current = reducedMeshConnections;
    return reducedMeshConnections;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageElement) return;

    const ctx = canvas.getContext("2d");
    canvas.width = imageElement.naturalWidth;
    canvas.height = imageElement.naturalHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imageElement, 0, 0, canvas.width, canvas.height);

    if (!landmarks || status === "idle") return;
    drawOverlay(ctx, landmarks, progress, getReducedMeshConnections());
  }, [imageElement, landmarks, progress, status]);

  const tryCompleteProgress = () => {
    if (completionStartedRef.current) return;
    if (!analysisReadyRef.current) return;
    if (progressValueRef.current < 98) return;

    completionStartedRef.current = true;
    completionTimerRef.current = window.setInterval(() => {
      const nextProgress = Math.min(progressValueRef.current + 1, 100);
      updateProgress(nextProgress);
      if (nextProgress >= 100) {
        window.clearInterval(completionTimerRef.current);
        completionTimerRef.current = null;
        setStatus("done");
      }
    }, 15);
  };

  const startProgressAnimation = () => {
    clearProgressTimers();
    analysisReadyRef.current = false;
    completionStartedRef.current = false;
    updateProgress(1);

    const animationStartAt = Date.now();
    stagedTimerRef.current = window.setInterval(() => {
      const elapsed = Math.min(Date.now() - animationStartAt, ANALYSIS_PROGRESS_DURATION_MS);
      updateProgress(getProgressByKeyframes(elapsed));

      if (elapsed >= ANALYSIS_PROGRESS_DURATION_MS) {
        window.clearInterval(stagedTimerRef.current);
        stagedTimerRef.current = null;
        tryCompleteProgress();
      }
    }, 40);
  };

  const runAnalysis = async (targetImageUrl) => {
    const runId = analysisRunIdRef.current + 1;
    analysisRunIdRef.current = runId;

    if (!targetImageUrl) {
      setErrorMessage(missingImageMessage);
      setStatus("error");
      return;
    }

    try {
      setStatus("analyzing");
      setErrorMessage("");
      setLandmarks(null);
      setSaveOverlayError("");
      setIsSavingOverlay(false);
      setIsQueueingJob(false);
      setQueueJobError("");
      setEnqueueErrorDetails(EMPTY_ENQUEUE_ERROR_DETAILS);
      setQueuedJobId("");
      setIsContinueFallbackUnlocked(false);
      setIsRedirectPending(false);
      setProgress(0);
      startProgressAnimation();

      assertImageUrlResolvable(targetImageUrl);
      const [, loadedImage] = await Promise.all([ensureMediaPipeReady(), loadImage(targetImageUrl)]);

      try {
        assertCanvasReadable(loadedImage);
      } catch (_) {
        throw new Error(
          "The image cannot be processed due to CORS restrictions. Add Access-Control-Allow-Origin for https://payment.color-analysis.pro on the image host."
        );
      }

      if (runId !== analysisRunIdRef.current) return;
      setImageElement(loadedImage);
      analyzedSourceUrlRef.current = buildAbsoluteUrl(targetImageUrl);
      usedDemoImageRunIdRef.current = isDemoImageUrl(targetImageUrl) ? runId : 0;
      const detectedLandmarks = await detectLandmarks(loadedImage);
      if (runId !== analysisRunIdRef.current) return;
      setLandmarks(detectedLandmarks);
      analysisReadyRef.current = true;
      tryCompleteProgress();
    } catch (error) {
      if (runId !== analysisRunIdRef.current) return;
      if (shouldRetryWithDemoImage(targetImageUrl, error)) {
        runAnalysis(DEFAULT_DEMO_IMAGE_PATH);
        return;
      }
      clearProgressTimers();
      setStatus("error");
      updateProgress(0);
      setErrorMessage(error?.message || "Face analysis failed.");
    }
  };

  useEffect(() => {
    if (!imageUrl) {
      analysisRunIdRef.current += 1;
      autoStartedUrlRef.current = "";
      clearProgressTimers();
      setImageElement(null);
      setLandmarks(null);
      setSaveOverlayError("");
      setQueueJobError("");
      setQueuedJobId("");
      setIsSavingOverlay(false);
      setIsQueueingJob(false);
      setIsContinueFallbackUnlocked(false);
      setIsRedirectPending(false);
      setStatus("error");
      updateProgress(0);
      setErrorMessage(missingImageMessage);
      return;
    }

    if (autoStartedUrlRef.current === analysisImageUrl) return;
    autoStartedUrlRef.current = analysisImageUrl;
    runAnalysis(analysisImageUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisImageUrl, imageUrl, missingImageMessage]);

  useEffect(() => {
    if (!imageElement || !landmarks) return;
    const currentRunId = analysisRunIdRef.current;
    if (!currentRunId) return;
    if (backendStartedRunIdRef.current === currentRunId) return;

    let cancelled = false;
    backendStartedRunIdRef.current = currentRunId;

    const saveOverlayAndQueue = async () => {
      setIsSavingOverlay(true);
      setSaveOverlayError("");
      setQueueJobError("");
      setEnqueueErrorDetails(EMPTY_ENQUEUE_ERROR_DETAILS);

      try {
        const jobId = createJobId();
        const overlayCanvas = createOverlayCanvas(
          imageElement,
          landmarks,
          getReducedMeshConnections() || window.FACEMESH_TESSELATION,
          100
        );
        const { blob: overlayBlob, mimeType: resolvedMimeType } = await canvasToUploadableBlob(overlayCanvas);
        const uploadResponse = await fetch(
          `${apiBase}${FACEMESH_V2_OVERLAY_UPLOAD_PATH}?job_id=${encodeURIComponent(jobId)}`,
          {
            method: "POST",
            headers: { "Content-Type": resolvedMimeType },
            body: overlayBlob,
          }
        );

        const uploadPayload = await uploadResponse.json();
        if (!uploadResponse.ok) {
          if (uploadResponse.status === 415) {
            throw new Error(
              uploadPayload?.error || `Failed to save overlay image. Uploaded MIME type: ${resolvedMimeType}.`
            );
          }
          throw new Error(uploadPayload?.error || "Failed to save overlay image.");
        }

        if (cancelled) return;
        uploadedRunIdRef.current = currentRunId;

        setIsQueueingJob(true);
        const resolvedSourceImageUrl = analyzedSourceUrlRef.current || buildAbsoluteUrl(imageUrl);
        const usingDemoImage = usedDemoImageRunIdRef.current === currentRunId;
        let queueResponse;
        try {
          queueResponse = await fetch(`${apiBase}${FACEMESH_V2_PROCESS_PATH}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              job_id: jobId,
              session_id: sessionIdRef.current || undefined,
              source_image_url: resolvedSourceImageUrl,
              facemesh_overlay_url: uploadPayload.url,
              client_reference_id: clientReferenceId,
              desc: serializeLandmarksCompact(landmarks),
              extension_ratio: FACEMESH_V2_EXTENSION_RATIO,
              page_path: pagePath,
              using_demo_image: usingDemoImage,
            }),
          });
        } catch (error) {
          const nextDetails = normalizeEnqueueErrorDetails({
            errorKind: "network_error",
            errorMessage: error?.message || "Failed to queue background analysis.",
          });
          setEnqueueErrorDetails(nextDetails);
          if (sessionIdRef.current) {
            sendFacemeshV2AnalyticsEvent({
              analyticsPrefix,
              event_name: enqueueFailedEventName,
              session_id: sessionIdRef.current,
              job_id: jobId,
              client_reference_id: clientReferenceId || undefined,
              page_path: pagePath,
              source_image_present: hasProvidedImageUrl,
              source_image_valid: hasValidProvidedImageUrl,
              metadata_json: {
                error_message: nextDetails.errorMessage,
                error_code: nextDetails.errorCode,
                error_stage: nextDetails.errorStage,
                http_status: nextDetails.httpStatus,
                error_kind: nextDetails.errorKind,
              },
            });
          }
          throw error;
        }

        const queueResponseText = await queueResponse.text();
        const queuePayload = parseJsonSafely(queueResponseText);
        if (!queueResponse.ok) {
          const nextDetails = normalizeEnqueueErrorDetails({
            errorCode: queuePayload?.error_code || "",
            errorStage: queuePayload?.error_stage || "",
            httpStatus: queueResponse.status,
            errorKind: "http_error",
            errorMessage: queuePayload?.error || queueResponseText || "Failed to queue background analysis.",
          });
          setEnqueueErrorDetails(nextDetails);
          if (sessionIdRef.current) {
            sendFacemeshV2AnalyticsEvent({
              analyticsPrefix,
              event_name: enqueueFailedEventName,
              session_id: sessionIdRef.current,
              job_id: queuePayload?.job_id || jobId,
              client_reference_id: clientReferenceId || undefined,
              page_path: pagePath,
              source_image_present: hasProvidedImageUrl,
              source_image_valid: hasValidProvidedImageUrl,
              metadata_json: {
                error_message: nextDetails.errorMessage,
                error_code: nextDetails.errorCode,
                error_stage: nextDetails.errorStage,
                http_status: nextDetails.httpStatus,
                error_kind: nextDetails.errorKind,
              },
            });
          }
          throw new Error(nextDetails.errorMessage);
        }

        if (!queuePayload) {
          const nextDetails = normalizeEnqueueErrorDetails({
            httpStatus: queueResponse.status,
            errorKind: "invalid_json",
            errorMessage: "Failed to parse enqueue response.",
          });
          setEnqueueErrorDetails(nextDetails);
          if (sessionIdRef.current) {
            sendFacemeshV2AnalyticsEvent({
              analyticsPrefix,
              event_name: enqueueFailedEventName,
              session_id: sessionIdRef.current,
              job_id: jobId,
              client_reference_id: clientReferenceId || undefined,
              page_path: pagePath,
              source_image_present: hasProvidedImageUrl,
              source_image_valid: hasValidProvidedImageUrl,
              metadata_json: {
                error_message: nextDetails.errorMessage,
                error_code: nextDetails.errorCode,
                error_stage: nextDetails.errorStage,
                http_status: nextDetails.httpStatus,
                error_kind: nextDetails.errorKind,
              },
            });
          }
          throw new Error(nextDetails.errorMessage);
        }

        if (cancelled) return;
        queuedRunIdRef.current = currentRunId;
        setEnqueueErrorDetails(EMPTY_ENQUEUE_ERROR_DETAILS);
        setQueuedJobId(queuePayload.job_id || jobId);
      } catch (error) {
        if (cancelled) return;
        backendStartedRunIdRef.current = 0;
        const message = error?.message || "Failed to save overlay image.";
        if (uploadedRunIdRef.current === currentRunId) {
          setQueueJobError(message);
        } else {
          setSaveOverlayError(message);
        }
      } finally {
        if (!cancelled) {
          setIsSavingOverlay(false);
          setIsQueueingJob(false);
        }
      }
    };

    saveOverlayAndQueue();

    return () => {
      cancelled = true;
    };
  }, [
    analyticsPrefix,
    apiBase,
    clientReferenceId,
    enqueueFailedEventName,
    hasProvidedImageUrl,
    hasValidProvidedImageUrl,
    imageElement,
    imageUrl,
    landmarks,
    pagePath,
  ]);

  useEffect(() => {
    if (status !== "done") return;
    if (hasQueuedBackend) {
      if (continueFallbackTimerRef.current) {
        window.clearTimeout(continueFallbackTimerRef.current);
        continueFallbackTimerRef.current = null;
      }
      return;
    }
    if (isContinueFallbackUnlocked) return;
    if (continueFallbackTimerRef.current) return;

    continueFallbackTimerRef.current = window.setTimeout(() => {
      continueFallbackTimerRef.current = null;
      setIsContinueFallbackUnlocked(true);
    }, CONTINUE_FALLBACK_DELAY_MS);
  }, [hasQueuedBackend, isContinueFallbackUnlocked, status]);

  useEffect(() => {
    if (!canContinue) return;
    const currentRunId = analysisRunIdRef.current;
    if (!currentRunId) return;
    if (autoRedirectedRunIdRef.current === currentRunId) return;

    autoRedirectedRunIdRef.current = currentRunId;
    setIsRedirectPending(true);

    autoRedirectTimerRef.current = window.setTimeout(() => {
      autoRedirectTimerRef.current = null;
      const nextUrl = buildContinueUrl(continueUrl, window.location.search);
      if (sessionIdRef.current) {
        sendFacemeshV2AnalyticsEvent({
          analyticsPrefix,
          event_name: continueEventName,
          session_id: sessionIdRef.current,
          job_id: queuedJobId || undefined,
          client_reference_id: clientReferenceId || undefined,
          page_path: pagePath,
          source_image_present: hasProvidedImageUrl,
          source_image_valid: hasValidProvidedImageUrl,
          metadata_json: {
            trigger: "auto_redirect",
            continue_url: nextUrl,
            has_queued_backend: hasQueuedBackend,
            fallback_unlocked: isContinueFallbackUnlocked,
            enqueue_error_code: hasQueuedBackend ? undefined : enqueueErrorDetails.errorCode || undefined,
            enqueue_error_stage: hasQueuedBackend ? undefined : enqueueErrorDetails.errorStage || undefined,
            enqueue_http_status: hasQueuedBackend ? undefined : enqueueErrorDetails.httpStatus,
            enqueue_error_kind: hasQueuedBackend ? undefined : enqueueErrorDetails.errorKind || undefined,
          },
          keepalive: true,
        });
      }
      window.location.assign(nextUrl);
    }, AUTO_REDIRECT_DELAY_MS);
  }, [
    analyticsPrefix,
    canContinue,
    clientReferenceId,
    continueEventName,
    continueUrl,
    hasProvidedImageUrl,
    hasQueuedBackend,
    hasValidProvidedImageUrl,
    enqueueErrorDetails,
    isContinueFallbackUnlocked,
    pagePath,
    queuedJobId,
  ]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F0] via-[#FFE6D1] to-white px-4 py-6 pb-28 sm:px-6 sm:py-8 sm:pb-8">
      <div className="mx-auto w-full max-w-[700px] rounded-2xl border border-[#7C3A8D]/15 bg-white p-4 shadow-sm sm:p-8">
        <h1 className="text-center text-lg font-bold leading-snug text-[#111827] sm:text-3xl">
          For accurate analysis,
          <br />
          we use advanced AI technology
        </h1>

        {(status === "analyzing" || status === "done") && (
          <div className="mt-4 rounded-xl border border-[#7C3A8D]/10 bg-gradient-to-r from-[#F8F6F0] to-white p-3 sm:mt-6 sm:p-5">
            <div className="flex items-end justify-between">
              <p className="text-xs font-semibold text-[#7C3A8D] sm:text-sm">Analysis progress</p>
              <p className="text-2xl font-extrabold leading-none text-[#111827] sm:text-3xl">{progress}%</p>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[#E5E7EB] sm:mt-3 sm:h-2.5">
              <div
                className="h-full rounded-full bg-[#7C3A8D] transition-all duration-150"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="mt-2 text-left text-sm font-semibold text-[#111827] sm:mt-3 sm:text-base">
              {getProgressLabel(progress, progressSteps)}
            </p>
            <div className="mt-3 space-y-1.5 sm:mt-4 sm:space-y-2">
              {progressSteps.map((step, index) => {
                const isCompleted = progress >= step.threshold;
                const isActive = index === currentStepIndex && status !== "done";
                return (
                  <div key={step.label} className="flex items-center gap-2 sm:gap-2.5">
                    <span
                      className={`inline-flex h-[18px] w-[18px] items-center justify-center rounded-full text-[10px] font-bold transition-colors sm:h-5 sm:w-5 sm:text-xs ${
                        isCompleted
                          ? "bg-[#7C3A8D] text-white"
                          : isActive
                          ? "bg-[#FFE6D1] text-[#7C3A8D]"
                          : "bg-[#E5E7EB] text-[#9CA3AF]"
                      }`}
                    >
                      {isCompleted ? "✓" : index + 1}
                    </span>
                    <span
                      className={`text-xs sm:text-sm ${
                        isCompleted || isActive ? "font-medium text-[#111827]" : "text-[#9CA3AF]"
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-4 overflow-hidden rounded-xl border border-[#7C3A8D]/10 bg-[#F8F6F0] shadow-sm sm:mt-6">
          {imageElement ? (
            <div className="flex max-h-[42vh] items-center justify-center sm:max-h-none">
              <canvas ref={canvasRef} className="block h-auto max-h-[42vh] w-auto max-w-full sm:max-h-none sm:w-full" />
            </div>
          ) : (
            <div className="flex aspect-[3/4] max-h-[42vh] items-center justify-center px-6 text-center text-sm font-medium text-[#6B7280] sm:max-h-none">
              Preparing your AI analysis...
            </div>
          )}
        </div>

        {status === "error" && (
          <div className="mt-5 rounded-lg border border-[#F4A896] bg-[#FFE6D1] p-3 text-sm text-[#7C3A8D]">
            {errorMessage}
          </div>
        )}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#7C3A8D]/10 bg-white/95 px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3 backdrop-blur-sm sm:px-6">
        <div className="mx-auto w-full max-w-[700px]">
          <button
            type="button"
            disabled
            className={`inline-flex h-12 w-full items-center justify-center rounded-md px-6 text-base font-bold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#7C3A8D] focus:ring-offset-2 disabled:cursor-not-allowed ${
              isRedirectPending ? "bg-[#7C3A8D] text-white" : "bg-[#D1D5DB] text-[#6B7280]"
            }`}
          >
            {isRedirectPending ? "Done" : "Finalizing analysis..."}
          </button>
        </div>
      </div>

      {(queueJobError || saveOverlayError) && (
        <div className="mx-auto w-full max-w-[700px] pt-20 sm:pt-24">
          {queueJobError && (
            <div className="mt-4 rounded-lg border border-[#F4A896] bg-[#FFF4F2] p-3 text-sm text-[#7C3A8D]">
              {queueJobError}
            </div>
          )}
          {saveOverlayError && (
            <div className="mt-4 rounded-lg border border-[#F4A896] bg-[#FFF4F2] p-3 text-sm text-[#7C3A8D]">
              {saveOverlayError}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FaceMeshAsyncV2Page;
